import { useConversationInput, useConversationStatus } from '@elevenlabs/react';
import { motion } from 'framer-motion';
import { MicOff, Mic } from 'lucide-react';

/**
 * MuteToggle — small circular glass button rendered in the top-right corner
 * while the ElevenLabs conversation is connected. Subtle, gallery-like,
 * never competes with the central Finish Conversation action.
 *
 * Must be rendered inside a <ConversationProvider>.
 */
export default function MuteToggle() {
  const { status } = useConversationStatus();
  const { isMuted, setMuted } = useConversationInput();

  if (status !== 'connected') return null;

  const Icon = isMuted ? MicOff : Mic;

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      onClick={() => setMuted(!isMuted)}
      aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
      data-testid="mute-toggle"
      data-clickable="true"
      className="fixed top-5 right-5 sm:top-6 sm:right-6 z-30 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
      style={{
        background: isMuted ? 'rgba(232,139,160,0.10)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${isMuted ? 'rgba(232,139,160,0.45)' : 'rgba(255,255,255,0.18)'}`,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: isMuted ? '#E88BA0' : 'rgba(245,245,245,0.7)',
      }}
      whileHover={{
        scale: 1.06,
        borderColor: isMuted ? 'rgba(232,139,160,0.8)' : 'rgba(155,230,244,0.7)',
        color: isMuted ? '#E88BA0' : '#9BE6F4',
      }}
      whileTap={{ scale: 0.94 }}
    >
      <Icon size={14} strokeWidth={1.6} />
    </motion.button>
  );
}
